import { SearchIcon, X } from "lucide-react";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { GlobalSearchTab } from "@/models/search/Search";
import { useGlobalSearchContext } from "@/providers/global-search/GlobalSearchContext";
import CopyLinkButton from "./CopyLinkButton";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import useSynonymGroups from "@/hooks/useSynonymGroups";
import useUserPreference from "@/hooks/useUserPreference";
import {
  buildSynonymDictionary,
  findCurrentWord,
  findSynonymMatches,
  SynonymMatch,
} from "@/lib/synonymMatching";
import {
  clampSynonymPopoverToViewport,
  estimateSynonymPopoverHeight,
} from "@/lib/clampSynonymPopoverToViewport";
import SynonymSuggestions from "@/pages/chat/components/SynonymSuggestions";
import debounce from "lodash/debounce";
import { parseSearchTags } from "@/pages/legislation/utils/tableUtils";
import SearchSyntaxHelper from "@/components/data-table/search-syntax-helper";
import { SearchTag } from "@/components/data-table/search-tag";
import { withClearedSearchText } from "@/pages/global-search/utils/searchUtils";

interface GlobalSearchBarProps {
  inputRef: React.RefObject<HTMLInputElement>;
  isFullPage?: boolean;
}

const GlobalSearchBar = ({
  inputRef,
  isFullPage = false,
}: GlobalSearchBarProps) => {
  const {
    debouncedSearch,
    inputValue,
    setInputValue,
    searchQuery,
    setSearchQuery,
  } = useGlobalSearchContext();

  // Synonym matching hooks
  const { useQueryUserDictionary } = useSynonymGroups();
  const { useQuerySynonymPreferences } = useUserPreference();
  const userDictionaryQuery = useQueryUserDictionary();
  const synonymPreferencesQuery = useQuerySynonymPreferences();

  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [synonymMatches, setSynonymMatches] = useState<SynonymMatch[]>([]);
  const [matchPosition, setMatchPosition] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [suggestionPosition, setSuggestionPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  // Build synonym dictionary from fetched data
  const synonymDictionary = useMemo(() => {
    return buildSynonymDictionary(
      userDictionaryQuery.data,
      synonymPreferencesQuery.data
    );
  }, [userDictionaryQuery.data, synonymPreferencesQuery.data]);

  // Calculate popover position based on cursor position for input element
  const calculateSuggestionPosition = useCallback(() => {
    const input = inputRef?.current;
    if (!input || !matchPosition) {
      return null;
    }

    try {
      // Since input doesn't support Range API directly, create a mirror div
      // that matches the input styling exactly and use Range API on it
      const inputStyle = window.getComputedStyle(input);
      const inputRect = input.getBoundingClientRect();
      const paddingTop = parseFloat(inputStyle.paddingTop) || 0;
      const paddingLeft = parseFloat(inputStyle.paddingLeft) || 0;

      // Create a mirror div that matches input exactly
      const mirrorDiv = document.createElement("div");
      mirrorDiv.style.position = "absolute";
      mirrorDiv.style.visibility = "hidden";
      mirrorDiv.style.font = inputStyle.font;
      mirrorDiv.style.fontSize = inputStyle.fontSize;
      mirrorDiv.style.fontFamily = inputStyle.fontFamily;
      mirrorDiv.style.fontWeight = inputStyle.fontWeight;
      mirrorDiv.style.letterSpacing = inputStyle.letterSpacing;
      mirrorDiv.style.whiteSpace = "nowrap";

      // Position mirror div exactly where input's content area starts
      mirrorDiv.style.top = `${inputRect.top + paddingTop}px`;
      mirrorDiv.style.left = `${inputRect.left + paddingLeft}px`;

      // Insert text with a span around the matched word to get its exact position
      const textBefore = inputValue.slice(0, matchPosition.start);
      const matchedWord = inputValue.slice(
        matchPosition.start,
        matchPosition.end
      );
      const textAfter = inputValue.slice(matchPosition.end);

      // Create text nodes and span for the matched word
      const beforeNode = document.createTextNode(textBefore);
      const wordSpan = document.createElement("span");
      wordSpan.textContent = matchedWord;
      const afterNode = document.createTextNode(textAfter);

      mirrorDiv.appendChild(beforeNode);
      mirrorDiv.appendChild(wordSpan);
      mirrorDiv.appendChild(afterNode);

      document.body.appendChild(mirrorDiv);

      // Get the bounding rectangle of the word span (already in viewport coordinates)
      const wordRect = wordSpan.getBoundingClientRect();

      // Clean up
      document.body.removeChild(mirrorDiv);

      const raw = {
        top: wordRect.bottom + 4,
        left: wordRect.left,
      };
      const anchor = {
        top: wordRect.top,
        left: wordRect.left,
        bottom: wordRect.bottom,
        right: wordRect.right,
      };
      return clampSynonymPopoverToViewport(raw, anchor, {
        maxPopoverHeight: estimateSynonymPopoverHeight(synonymMatches.length),
      });
    } catch (error) {
      logger.warn("Failed to calculate synonym suggestion position", {
        operation: "positionSearchSynonymSuggestions",
        error,
      });
      const inputRect = input.getBoundingClientRect();
      const raw = {
        top: inputRect.bottom + 4,
        left: inputRect.left,
      };
      return clampSynonymPopoverToViewport(
        raw,
        {
          top: inputRect.top,
          left: inputRect.left,
          bottom: inputRect.bottom,
          right: inputRect.right,
        },
        { maxPopoverHeight: estimateSynonymPopoverHeight(synonymMatches.length) }
      );
    }
  }, [inputValue, matchPosition, inputRef, synonymMatches.length]);

  // Debounced matching function
  const performMatchingRef = useRef(
    debounce(
      (
        text: string,
        cursorPos: number,
        dictionary: typeof synonymDictionary,
        preferences: typeof synonymPreferencesQuery.data
      ) => {
        // Only match if synonym matching is active
        if (!preferences?.synonymMatchingActive) {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
          return;
        }

        const { start, end, word } = findCurrentWord(text, cursorPos);

        // Don't show suggestions if word is empty or too short
        if (!word || word.length < 2) {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
          return;
        }

        // Don't show suggestions if cursor is in the middle of a word (not at the end)
        if (cursorPos < end) {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
          return;
        }

        const matches = findSynonymMatches(word, dictionary);

        if (matches.length > 0) {
          setSynonymMatches(matches);
          setMatchPosition({ start, end });
          setIsSuggestionsOpen(true);
        } else {
          setIsSuggestionsOpen(false);
          setSynonymMatches([]);
          setMatchPosition(null);
        }
      },
      150
    )
  );

  const performMatching = useCallback(
    (text: string, cursorPos: number) => {
      performMatchingRef.current(
        text,
        cursorPos,
        synonymDictionary,
        synonymPreferencesQuery.data
      );
    },
    [synonymDictionary, synonymPreferencesQuery.data]
  );

  // Update suggestion position when match position changes
  useEffect(() => {
    if (isSuggestionsOpen && matchPosition) {
      const position = calculateSuggestionPosition();
      setSuggestionPosition(position);
    }
  }, [isSuggestionsOpen, matchPosition, calculateSuggestionPosition]);

  // Update position on scroll and resize
  useEffect(() => {
    if (!isSuggestionsOpen || !matchPosition) {
      return;
    }

    const updatePosition = () => {
      const position = calculateSuggestionPosition();
      if (position) {
        setSuggestionPosition(position);
      }
    };

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isSuggestionsOpen, matchPosition, calculateSuggestionPosition]);

  // Handle text and cursor changes
  useEffect(() => {
    if (inputValue && cursorPosition >= 0) {
      performMatching(inputValue, cursorPosition);
    } else {
      setIsSuggestionsOpen(false);
      setSynonymMatches([]);
      setMatchPosition(null);
    }
  }, [inputValue, cursorPosition, performMatching]);

  // Cleanup debounce on unmount
  useEffect(() => {
    const debouncedFn = performMatchingRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  // Reset selected index when suggestions close or matches change
  useEffect(() => {
    if (!isSuggestionsOpen) {
      setSelectedIndex(-1);
    }
  }, [isSuggestionsOpen]);

  useEffect(() => {
    if (synonymMatches.length > 0 && isSuggestionsOpen) {
      setSelectedIndex(0); // Start with first item selected
    }
  }, [synonymMatches.length, isSuggestionsOpen]);

  // Handle synonym selection
  const handleSynonymSelect = useCallback(
    (canonicalTerm: string) => {
      if (!matchPosition) {
        return;
      }

      const before = inputValue.slice(0, matchPosition.start);
      const after = inputValue.slice(matchPosition.end);
      const newValue = before + canonicalTerm + after;
      const newCursorPosition = matchPosition.start + canonicalTerm.length;

      setInputValue(newValue);
      debouncedSearch(newValue, searchQuery?.tab || GlobalSearchTab.All);
      setCursorPosition(newCursorPosition);
      setIsSuggestionsOpen(false);
      setSynonymMatches([]);
      setMatchPosition(null);
      setSelectedIndex(-1);

      // Set cursor position in input
      setTimeout(() => {
        const input = inputRef?.current;
        if (input) {
          input.setSelectionRange(newCursorPosition, newCursorPosition);
          input.focus();
        }
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue, matchPosition, inputRef, debouncedSearch, searchQuery?.tab]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newCursorPosition = e.target.selectionStart || 0;
    setInputValue(value);
    setCursorPosition(newCursorPosition);
    const currentTab = searchQuery?.tab || GlobalSearchTab.All;
    debouncedSearch(value, currentTab);
  };

  const handleInputSelectionChange = () => {
    if (inputRef.current) {
      setCursorPosition(inputRef.current.selectionStart || 0);
    }
  };

  // Remove a search tag by index
  const handleRemoveTag = useCallback(
    (index: number) => {
      const newTags = [...(searchQuery?.searchTags || [])];
      newTags.splice(index, 1);
      setSearchQuery({
        ...searchQuery,
        searchTags: newTags,
      });
    },
    [searchQuery, setSearchQuery]
  );

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle keyboard navigation when suggestions are open
    if (isSuggestionsOpen && synonymMatches.length > 0) {
      // Intercept arrow keys to navigate suggestions
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => {
          const next = prev < synonymMatches.length - 1 ? prev + 1 : 0;
          return next;
        });
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : synonymMatches.length - 1;
          return next;
        });
        return;
      }

      // Handle Tab to select focused item
      if (e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const indexToSelect = selectedIndex >= 0 ? selectedIndex : 0;
        if (synonymMatches[indexToSelect]) {
          handleSynonymSelect(synonymMatches[indexToSelect].canonicalTerm);
          setSelectedIndex(-1);
          return;
        }
      }

      // Close suggestions on Escape
      if (e.key === "Escape") {
        e.preventDefault();
        setIsSuggestionsOpen(false);
        setSelectedIndex(-1);
        return;
      }
    }

    // Handle Backspace to remove last tag when input is empty
    if (
      e.key === "Backspace" &&
      inputValue === "" &&
      searchQuery?.searchTags &&
      searchQuery.searchTags.length > 0
    ) {
      handleRemoveTag(searchQuery.searchTags.length - 1);
      return;
    }

    // Handle Enter to submit (existing logic)
    if (e.key === "Enter" && inputValue.trim() !== "") {
      const searchTags = parseSearchTags(inputValue);
      setSearchQuery({
        ...searchQuery,
        searchTerm: "",
        searchTags: [...(searchQuery?.searchTags || []), ...searchTags],
      });

      setInputValue("");
    }
  };

  useEffect(() => {
    setInputValue(searchQuery?.searchTerm || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery?.searchTerm]);

  // Focus the input when clicking on the container
  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const hasTags = searchQuery?.searchTags && searchQuery.searchTags.length > 0;

  return (
    <div className="relative">
      <SearchIcon
        className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground z-10"
        size={16}
      />
      <div className="relative">
        {/* Container that looks like an input with inline tags */}
        <div
          className={cn(
            "flex items-center flex-wrap gap-1 pl-8 pr-24 py-2.5 min-h-11 bg-background border shadow-xs w-full cursor-text",
            isFullPage ? "rounded-sm" : "rounded-lg"
          )}
          onClick={handleContainerClick}
        >
          {/* Inline search tags */}
          {searchQuery?.searchTags?.map((tag, index) => (
            <SearchTag
              key={index}
              tag={tag}
              variant="inline"
              onRemove={() => handleRemoveTag(index)}
            />
          ))}
          {/* Auto-expanding input */}
          <input
            ref={inputRef}
            placeholder={hasTags ? "" : "Søg"}
            className="flex-1 min-w-[60px] outline-none bg-transparent text-sm"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={handleInputSelectionChange}
            onClick={(e) => {
              e.stopPropagation();
              handleInputSelectionChange();
            }}
            autoFocus
          />
        </div>
        {isSuggestionsOpen && synonymMatches.length > 0 && (
          <SynonymSuggestions
            matches={synonymMatches}
            isOpen={isSuggestionsOpen}
            onClose={() => {
              setIsSuggestionsOpen(false);
              setSelectedIndex(-1);
            }}
            onSelect={handleSynonymSelect}
            anchorElement={inputRef.current}
            position={suggestionPosition || undefined}
            selectedIndex={selectedIndex}
          />
        )}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.05 }}
        className="absolute right-0 top-0 h-full flex w-24 items-center justify-end gap-2 pr-2"
      >
        {(inputValue || hasTags) && (
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={() => {
              // Clear both input value and search tags to return to initial view
              setInputValue("");
              setSearchQuery(withClearedSearchText(searchQuery));
            }}
            title="Ryd søgning"
          >
            <X size={14} />
          </Button>
        )}
        <SearchSyntaxHelper
          buttonClassName="size-6"
          popoverAlign="end"
          isSearchBar={true}
        />

        <PermissionGate allOf={[Permission.CreateSearchShare]}>
          <CopyLinkButton
            searchQuery={searchQuery}
            buttonClassName="size-6 group"
          />
        </PermissionGate>
      </motion.div>
    </div>
  );
};

export default GlobalSearchBar;
